import { ToClientCommand, ToServerCommand } from "./constants.js";
import { ClientRequestBase } from "./data_structures/to_server/client_request_base.js";
import { MessageReader } from "./message_handlers/message_reader.js";
import { MessageWriter } from "./message_handlers/message_writer.js";
import { WebSocketClient } from "./web_socket_client.js";
import { UtilityPlugin } from "../plugins/utility_plugin.js";
import { CrashBreadcrumbs } from "../crash/breadcrumbs.js";

type HandlerFn = ( reader: MessageReader ) => void;

export class ServerController
{
  private static readonly RECONNECT_INTERVAL_MS = 3_000;

  private readonly client = new WebSocketClient();
  private readonly handlers = new Map<ToClientCommand, HandlerFn>();
  private readonly reconnect_handlers: Array<() => void> = [];
  private readonly disconnect_handlers: Array<() => void> = [];
  private readonly next_connection_handlers: Array<() => void> = [];
  private connect_resolve: ( () => void ) | null = null;
  private connect_promise = new Promise<void>( resolve => { this.connect_resolve = resolve; } );
  private reconnect_timer: ReturnType<typeof setTimeout> | null = null;
  private online_listener: ( () => void ) | null = null;
  private is_connecting = false;
  private reconnect_enabled = true;

  constructor( private readonly url: string ) {}

  public onNextConnection( fn: () => void ): void
  {
    this.next_connection_handlers.push( fn );
  }

  public waitForConnectionWithTimeout( timeout_ms: number ): Promise<boolean>
  {
    return new Promise( resolve =>
    {
      const timer = setTimeout( () => resolve( false ), timeout_ms );
      void this.connect_promise.then( () =>
      {
        clearTimeout( timer );
        resolve( true );
      } );
    } );
  }

  public onReconnect( fn: () => void ): void
  {
    this.reconnect_handlers.push( fn );
  }

  public onDisconnect( fn: () => void ): void
  {
    this.disconnect_handlers.push( fn );
  }

  public connect(): void
  {
    this.reconnect_enabled = true;
    void UtilityPlugin.reportAppStart( 'server_connect', 'start' );
    this.createConnection( false );
  }

  public disconnect(): void
  {
    this.reconnect_enabled = false;
    this.stopReconnect();
    this.disconnect_handlers.forEach( fn => fn() );
    this.client.disconnect();
    this.resetConnectionPromise();
  }

  public sendRequest( req: ClientRequestBase | null, command: ToServerCommand ): boolean
  {
    try
    {
      return this.client.send( MessageWriter.serializeMessage( command, req ) );
    }
    catch ( error )
    {
      console.warn( `[WebSocket] failed to serialize command 0x${command.toString( 16 )}`, error );
      return false;
    }
  }

  public on( command: ToClientCommand, fn: HandlerFn ): void
  {
    this.handlers.set( command, fn );
  }

  public clearHandlers(): void
  {
    this.handlers.clear();
  }

  private createConnection( is_reconnect: boolean ): void
  {
    if ( this.is_connecting ) return;
    this.is_connecting = true;

    try
    {
      this.client.connect(
        this.url,
        () =>
        {
          this.is_connecting = false;
          this.stopReconnect();
          this.connect_resolve?.();
          this.next_connection_handlers.splice( 0 ).forEach( fn => fn() );
          if ( is_reconnect ) this.reconnect_handlers.forEach( fn => fn() );
          CrashBreadcrumbs.add( 'ws', is_reconnect ? 'reconnected' : 'connected' );
        },
        reader => this.handleMessage( reader ),
        () =>
        {
          CrashBreadcrumbs.add( 'ws', 'disconnected' );
          this.handleDisconnect();
        },
      );
    }
    catch ( error )
    {
      this.is_connecting = false;
      console.warn( `[WebSocket] failed to connect: ${this.url}`, error );
      this.startReconnect();
    }
  }

  private handleMessage( reader: MessageReader ): void
  {
    if ( reader.getBufferLength() < 4 )
    {
      console.warn( '[WebSocket] ignored message shorter than header' );
      return;
    }

    const command = reader.readCommand() as ToClientCommand;
    const body_length = reader.readBodyLength();
    if ( body_length + 4 > reader.getBufferLength() )
    {
      console.warn( `[WebSocket] invalid body length for command 0x${command.toString( 16 )}` );
      return;
    }

    const handler = this.handlers.get( command );
    if ( handler ) handler( reader );
    else console.warn( `[WebSocket] no handler for command 0x${command.toString( 16 )}` );
  }

  private handleDisconnect(): void
  {
    this.is_connecting = false;
    this.resetConnectionPromise();
    this.disconnect_handlers.forEach( fn => fn() );
    if ( this.reconnect_enabled ) this.startReconnect();
  }

  private resetConnectionPromise(): void
  {
    this.connect_promise = new Promise( resolve => { this.connect_resolve = resolve; } );
  }

  private startReconnect(): void
  {
    this.stopReconnect();
    this.online_listener = () => this.attemptReconnect();
    window.addEventListener( 'online', this.online_listener );
    this.reconnect_timer = setTimeout( () => this.attemptReconnect(), ServerController.RECONNECT_INTERVAL_MS );
  }

  private stopReconnect(): void
  {
    if ( this.online_listener )
    {
      window.removeEventListener( 'online', this.online_listener );
      this.online_listener = null;
    }
    if ( this.reconnect_timer )
    {
      clearTimeout( this.reconnect_timer );
      this.reconnect_timer = null;
    }
  }

  private attemptReconnect(): void
  {
    if ( !this.reconnect_enabled || this.is_connecting ) return;
    this.stopReconnect();
    console.info( '[WebSocket] reconnecting...' );
    this.createConnection( true );
  }
}
